import React from 'react';
import { Header } from './Header';
import { Footer } from './Footer';

export const OurStory = ({ headerProps, footerProps = {}, bannerOffset = 0 }) => {
  return (
    <div
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#111111',
        lineHeight: 1.8,
        overflowX: 'hidden',
        background: '#fff'
      }}
    >
      <Header {...headerProps} />

      {/* Hero */}
      <section
        className="our-story-hero"
        style={{
          paddingTop: `${160 + bannerOffset}px`,
          paddingRight: '24px',
          paddingBottom: '60px',
          paddingLeft: '24px',
          background: 'linear-gradient(180deg, #F9F7F4 0%, #fff 100%)',
          maxWidth: '720px',
          margin: '0 auto'
        }}
      >
        <h1
          style={{
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: 'clamp(48px, 8vw, 72px)',
            fontWeight: '600',
            lineHeight: 1.1,
            margin: '0 0 24px',
            color: '#111111',
            textAlign: 'left'
          }}
        >
          Our Story
        </h1>
      </section>

      {/* Main Content */}
      <article
        style={{
          maxWidth: '720px',
          margin: '0 auto',
          padding: '40px 24px 100px'
        }}
      >
        {/* Opening */}
        <section style={{ marginBottom: '64px', textAlign: 'left' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(28px, 5vw, 36px)',
              fontWeight: '600',
              lineHeight: 1.3,
              margin: '0 0 32px',
              color: '#111111',
              textAlign: 'left'
            }}
          >
            We are <HighlightText>real patients</HighlightText>.
          </h2>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            Long before we (Jen and Ronnie) were founders, researchers, and advisors, we were learning firsthand how chronic illness shapes daily life.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: 0 }}>
            Our patient experiences—and what they taught us—continue to guide everything we do.
          </p>
        </section>

        {/* Divider */}
        <Divider />

        {/* Shaped by experience */}
        <section style={{ marginBottom: '64px', textAlign: 'left' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(24px, 4vw, 32px)',
              fontWeight: '600',
              lineHeight: 1.3,
              margin: '0 0 32px',
              color: '#111111',
              textAlign: 'left'
            }}
          >
            Shaped by real patient experience
          </h2>

          {/* Jen's story */}
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            For <NameHighlight>Jen Horonjeff</NameHighlight>, healthcare has been part of life for as long as she can remember.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            Diagnosed as a baby with juvenile idiopathic arthritis and uveitis—and later with additional autoimmune diseases and a brain tumor—healthcare has been a constant in her life.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            Growing up with a physically unpredictable disease, Jen was surrounded by assumptions—what her body could do, what was considered realistic, and what her future should look like. Those assumptions rarely aligned with what mattered most to her. Long before starting a company, she became a professional dancer, living inside her body <HighlightText>on her own terms</HighlightText>, even when the healthcare system underestimated her abilities.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            That same drive also pulled her toward research. After earning a bachelor's degree in dance and biomedical engineering, Jen went on to complete both a master's degree and a PhD in human factors and environmental medicine, focusing on patient priorities and the real-world factors that shape the patient experience. Along the way, she also served as an advisor to the FDA, sitting on advisory committees reviewing applications for new treatment.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            Jen's work has always lived at the intersection of <HighlightText>lived experience, research rigor, and real-world impact</HighlightText>.
          </p>

          {/* Divider between founders */}
          <FounderDivider />

          {/* Ronnie's story */}
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            For <NameHighlight>Ronnie Sharpe</NameHighlight>, the story also began early—and with uncertainty.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            Diagnosed with cystic fibrosis as a baby, he was given a difficult prognosis and few clear answers about what the future might hold. As he got older, something became clear to him—not as a sudden insight, but through lived experience. Patients were often left to figure things out on their own, especially in rare disease communities where information, support, and connection were limited or nonexistent.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            That realization led him to found <strong>CysticLife.org</strong>, an early rare disease social network designed to help people share experiences, find support, and get help where little existed. Alongside that work, Ronnie consulted with organizations seeking to engage patients, often discovering that good intentions alone weren't enough to create meaningful, respectful involvement.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            At the center of all of it is a deep commitment to improving the lives of others. That commitment has taken many forms—from creating a coffee shop within a homeless shelter to foster connection and dignity, to opening his home as a foster parent, caring for more than fifteen babies in need.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: 0 }}>
            For Ronnie, <HighlightText>community isn't a concept</HighlightText>. It's something you build, protect, and show up for—day after day.
          </p>
        </section>

        {/* Divider */}
        <Divider />

        {/* The moment */}
        <section style={{ marginBottom: '64px', textAlign: 'left' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(24px, 4vw, 32px)',
              fontWeight: '600',
              lineHeight: 1.3,
              margin: '0 0 32px',
              color: '#111111',
              textAlign: 'left'
            }}
          >
            The moment everything changed
          </h2>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            Jen and Ronnie met years before starting a company.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            Their paths crossed at the first annual meeting hosted by the <strong>Patient-Centered Outcomes Research Institute (PCORI)</strong>—Ronnie as an award recipient, Jen as an advisor helping guide and evaluate patient-centered research. They connected not as founders, but as peers who shared a similar frustration.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            Patients were being asked—again and again—to share deeply personal experiences. But the process was often inefficient, extractive, and poorly designed. People gave their time, their stories, and their expertise, yet were rarely treated as experts or compensated fairly. And the insights collected didn't always reflect the reality of daily life.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            They stayed in touch. And over time, that shared frustration turned into a shared question:
          </p>
          <blockquote
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(20px, 3vw, 24px)',
              fontStyle: 'italic',
              color: '#111111',
              margin: '32px 0',
              padding: '0 24px',
              borderLeft: '3px solid #FABEC0'
            }}
          >
            What if sharing lived experience were <HighlightText>easier, more respectful, and built for patients first</HighlightText>—without sacrificing rigor or credibility?
          </blockquote>
        </section>

        {/* Divider */}
        <Divider />

        {/* Building */}
        <section style={{ marginBottom: '64px', textAlign: 'left' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(24px, 4vw, 32px)',
              fontWeight: '600',
              lineHeight: 1.3,
              margin: '0 0 32px',
              color: '#111111',
              textAlign: 'left'
            }}
          >
            Building with real patients
          </h2>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            That question led to the founding of <strong>Savvy Cooperative</strong> in 2016.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            At the start, the goal was simple: make it easier for people to share their real experiences as patients—and make sure they were treated with respect when they did. But as we worked with patients and with healthcare companies across pharma, digital health, research, and policy, the work naturally expanded.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            We saw how much the quality of insight depends on <em>who</em> you're talking to—how connecting with verified patients changes not just the data, but the decisions made from it. We learned that research has to be fit for purpose, designed with an understanding of patients' lives, energy, and priorities. And we saw, repeatedly, that how patient experience data is used—today and in the future—has real consequences.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: 0 }}>
            Over time, the work became less about individual projects and more about building a thoughtful, responsible way of understanding patient experience—one rooted in <HighlightText>trust, rigor, and long-term impact</HighlightText>. That perspective is what shaped Real Patients.
          </p>
        </section>

        {/* Divider */}
        <Divider />

        {/* Why healthcare needs Real Patients */}
        <section style={{ marginBottom: '64px', textAlign: 'left' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(24px, 4vw, 32px)',
              fontWeight: '600',
              lineHeight: 1.3,
              margin: '0 0 32px',
              color: '#111111',
              textAlign: 'left'
            }}
          >
            Why healthcare needs Real Patients
          </h2>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            After nearly a decade of doing this work, one thing became increasingly clear: the most important differentiator in healthcare isn't technology alone. It's the quality, context, and reality of the patient experience behind the data.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            As healthcare has become more data-driven, patient experience is increasingly summarized, abstracted, or inferred. Decisions are made using proxies for patients rather than direct connection to the people living with disease. Who is included, how experience is captured, and how it's used now shapes everything from clinical development to emerging AI-driven tools.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            The name <strong>Real Patients</strong> reflects what has long been at the core of our work. It's not a departure from Savvy Cooperative, but a clearer expression of what the work demands.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: 0 }}>
            This work is personal for Jen and Ronnie, and it always has been. Our experience—both personally and professionally—informs how we listen, how we design the work, and how seriously we take the responsibility that comes with carrying patient experience forward.
          </p>
          <p
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: '20px',
              fontWeight: '600',
              color: '#111111',
              margin: '32px 0 0',
              lineHeight: 1.6
            }}
          >
            We believe the future of healthcare should be <HighlightText>shaped—and led—by real patients</HighlightText>.
          </p>
        </section>

        {/* Divider */}
        <Divider />

        {/* Brand note */}
        <section style={{ marginBottom: '48px', textAlign: 'left' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(20px, 3vw, 24px)',
              fontWeight: '600',
              lineHeight: 1.3,
              margin: '0 0 24px',
              color: '#111111',
              textAlign: 'left'
            }}
          >
            A Note on the Brand
          </h2>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            Our visual identity reflects how we work.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: '0 0 24px' }}>
            The <HighlightText>highlighter elements</HighlightText> are a nod to qualitative research coding—the process of working directly from people's own words and identifying the themes and priorities that emerge from real experiences, not assumptions.
          </p>
          <p style={{ fontSize: '18px', color: '#4A4A4A', margin: 0 }}>
            We bring that level of care because we know, as real patients ourselves, how much it matters to have your experience heard and respected.
          </p>
        </section>

        {/* Sign-off */}
        <section
          style={{
            paddingTop: '32px'
          }}
        >
          <p
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: '20px',
              color: '#4A4A4A',
              margin: '0 0 32px',
              fontStyle: 'italic'
            }}
          >
            We hope you'll join us in listening more closely to <HighlightText>real patients</HighlightText>.
          </p>

          <p
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: '24px',
              fontWeight: '600',
              color: '#111111',
              margin: '0 0 40px'
            }}
          >
            — Jen & Ronnie
          </p>

          {/* Founder photos */}
          <div
            className="founder-photos"
            style={{
              display: 'flex',
              gap: '40px',
              justifyContent: 'center',
              alignItems: 'flex-start'
            }}
          >
            <div style={{ flex: '0 0 auto', textAlign: 'center' }}>
              <img
                src="/jen-portrait.png"
                alt="Jen Horonjeff"
                style={{
                  width: '120px',
                  height: '140px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  marginBottom: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              />
              <p style={{
                fontSize: '15px',
                color: '#111111',
                margin: 0,
                fontWeight: '600',
                fontFamily: '"Source Serif Pro", Georgia, serif'
              }}>Jen Horonjeff, PhD</p>
              <p style={{
                fontSize: '13px',
                color: '#666666',
                margin: '2px 0 0'
              }}>Co-Founder & CEO</p>
            </div>
            <div style={{ flex: '0 0 auto', textAlign: 'center' }}>
              <img
                src="/ronnie-portrait.png"
                alt="Ronnie Sharpe"
                style={{
                  width: '120px',
                  height: '140px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  marginBottom: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              />
              <p style={{
                fontSize: '15px',
                color: '#111111',
                margin: 0,
                fontWeight: '600',
                fontFamily: '"Source Serif Pro", Georgia, serif'
              }}>Ronnie Sharpe</p>
              <p style={{
                fontSize: '13px',
                color: '#666666',
                margin: '2px 0 0'
              }}>Co-Founder & COO</p>
            </div>
          </div>
        </section>
      </article>

      <Footer {...footerProps} />

      <style>{`
        @media (max-width: 600px) {
          .our-story-hero {
            padding-top: ${140 + bannerOffset}px !important;
          }
          article {
            padding-left: 20px !important;
            padding-right: 20px !important;
          }
          .founder-photos {
            gap: 24px !important;
          }
          .founder-photos img {
            width: 100px !important;
            height: 120px !important;
          }
        }
      `}</style>
    </div>
  );
};

// Highlighter-style divider
const Divider = () => (
  <div
    style={{
      height: '8px',
      background: 'linear-gradient(90deg, #FABEC0 0%, #F7C08A 50%, #FBF4BC 100%)',
      margin: '48px auto 64px',
      maxWidth: '160px',
      borderRadius: '4px',
      opacity: 0.5
    }}
  />
);

// Divider between founder sections - styled like a highlighter stroke
const FounderDivider = () => (
  <div
    style={{
      height: '8px',
      background: 'linear-gradient(90deg, #FABEC0 0%, #F7C08A 50%, #FBF4BC 100%)',
      margin: '48px auto',
      maxWidth: '100px',
      borderRadius: '4px',
      opacity: 0.5
    }}
  />
);

// Highlighter text emphasis
const HighlightText = ({ children }) => (
  <span
    style={{
      background: 'linear-gradient(180deg, transparent 50%, rgba(247, 192, 138, 0.4) 50%)',
      padding: '0 2px'
    }}
  >
    {children}
  </span>
);

// Name highlight with watercolor effect
const NameHighlight = ({ children }) => (
  <span style={{ position: 'relative', display: 'inline' }}>
    <span
      style={{
        position: 'absolute',
        left: '-2px',
        right: '-2px',
        bottom: '2px',
        height: '8px',
        background: 'linear-gradient(90deg, #FABEC0 0%, #F7C08A 100%)',
        opacity: 0.4,
        zIndex: -1,
        borderRadius: '2px'
      }}
    />
    <strong style={{ position: 'relative', fontWeight: '600', color: '#111111' }}>{children}</strong>
  </span>
);

export default OurStory;
